import styles from 'css/src/components/codeo/CostSummary.module.scss';

import React from 'react';

import { formatUsd, sumUsageCost, type UsageCost, usageCost } from './usageCost';

import type { Usage } from 'js/src/agent/types';
import Tooltip from 'js/src/components/common/ui/Tooltip';

const fmtTokens = (n: number) => n.toLocaleString();

// One "<label> (<tokens> tok)  <cost>" row in the hover breakdown. Rendered only when there are
// tokens. Each side stays on one line so the two columns don't wrap or collide.
function BreakdownRow({ label, tokens, cost }: { label: string; tokens: number; cost: number }) {
    if (tokens <= 0) return null;
    return (
        <div className="d-flex justify-content-between" style={{ gap: 24, whiteSpace: 'nowrap' }}>
            <span>
                {label} ({fmtTokens(tokens)} tok)
            </span>
            <span>{formatUsd(cost)}</span>
        </div>
    );
}

function CostBreakdown({ cost }: { cost: UsageCost }) {
    return (
        <div style={{ fontSize: 12, textAlign: 'left' }}>
            <div
                className="d-flex justify-content-between"
                style={{ gap: 24, fontWeight: 600, marginBottom: 4, whiteSpace: 'nowrap' }}
            >
                <span>Total</span>
                <span>{formatUsd(cost.total)}</span>
            </div>
            <BreakdownRow label="Input" tokens={cost.freshInputTokens} cost={cost.freshInput} />
            <BreakdownRow
                label="Cached input"
                tokens={cost.cacheReadTokens}
                cost={cost.cacheRead}
            />
            <BreakdownRow
                label="Cache write"
                tokens={cost.cacheWriteTokens}
                cost={cost.cacheWrite}
            />
            <BreakdownRow label="Output" tokens={cost.outputTokens} cost={cost.output} />
        </div>
    );
}

// Total $ spent, with a hover breakdown of input / cached input / output tokens and their costs.
// Pass one part per model so multi-model flows (e.g. codeo's Opus conversation + its Sonnet brand
// analyzer) price each piece at its own rate before summing. By default a zero-cost turn renders
// nothing; pass showWhenZero to keep the "$0 total" line visible (e.g. the chat input footer).
export function CostSummary({
    parts,
    showWhenZero = false,
    showTotalLabel = true,
}: {
    parts: Array<{ usage: Usage; model?: string }>;
    showWhenZero?: boolean;
    // Whether to append the word "total" after the amount. The full breakdown is
    // always available on hover; callers with a tight footer can hide the label.
    showTotalLabel?: boolean;
}) {
    const cost = sumUsageCost(parts.map((p) => usageCost(p.usage, p.model)));
    if (cost.total <= 0 && !showWhenZero) return null;

    return (
        <Tooltip title={<CostBreakdown cost={cost} />} className={styles['cost-breakdown-tooltip']}>
            <div
                style={{ fontSize: 12, color: '#475569', cursor: 'default', width: 'fit-content' }}
            >
                <span style={{ fontWeight: 600 }}>{formatUsd(cost.total)}</span>
                {showTotalLabel ? ' total' : ''}
            </div>
        </Tooltip>
    );
}
